import { useEffect, useState } from 'react';
import { api, errorMessage } from '../api/client';
import type { Disease, WheelSegment } from '../api/types';
import { useResource } from '../api/useResource';
import { NumberInput, Panel, Select, Spinner, Status } from '../components/ui';

interface DraftSegment {
  index: number;
  diseaseSlug: string;
  movement: number;
  weight: number;
}

export function WheelPage() {
  const wheel = useResource(() => api<{ wheel: WheelSegment[] }>('/v1/admin/wheel'));
  const diseases = useResource(() => api<{ diseases: Disease[] }>('/v1/admin/diseases'));
  const [draft, setDraft] = useState<DraftSegment[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!wheel.data) return;
    setDraft(
      wheel.data.wheel.map((segment) => ({
        index: segment.index,
        diseaseSlug: segment.diseaseSlug,
        movement: segment.movement,
        weight: segment.weight,
      })),
    );
  }, [wheel.data]);

  const diseaseOptions = (diseases.data?.diseases ?? []).map((disease) => ({
    value: disease.slug,
    label: disease.name,
  }));

  const totalWeight = draft.reduce((sum, segment) => sum + (segment.weight || 0), 0);

  function update(index: number, patch: Partial<DraftSegment>) {
    setDraft(draft.map((segment, i) => (i === index ? { ...segment, ...patch } : segment)));
  }

  /** Indexes stay contiguous from 0 — a gap is meaningless on a physical wheel. */
  function reindex(segments: DraftSegment[]): DraftSegment[] {
    return segments.map((segment, i) => ({ ...segment, index: i }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      await api('/v1/admin/wheel', {
        method: 'PUT',
        body: { segments: reindex(draft) },
      });

      setMessage(`Wheel replaced — ${draft.length} segments.`);
      await wheel.reload();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  if ((wheel.loading && !wheel.data) || (diseases.loading && !diseases.data)) return <Spinner />;
  if (wheel.error) return <Status error={wheel.error} />;

  return (
    <Panel
      title="Wheel"
      actions={
        <>
          <span className="muted">
            {draft.length} segments · total weight {totalWeight}
          </span>
          <button className="button" onClick={() => void save()} disabled={saving || draft.length === 0}>
            {saving ? 'Saving…' : 'Replace wheel'}
          </button>
        </>
      }
    >
      <p className="muted">
        One spin yields both a movement number and a disease (spec 2.3), so the wheel is saved
        whole rather than segment by segment — a bad slug or a duplicate index leaves the current
        cut untouched. The current cut is a placeholder pending the wheel artwork.
      </p>

      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Disease</th>
            <th>Movement</th>
            <th>Weight</th>
            <th>Odds</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {draft.map((segment, i) => (
            <tr key={i}>
              <td>{i}</td>
              <td>
                <Select
                  value={segment.diseaseSlug}
                  options={diseaseOptions}
                  onChange={(diseaseSlug) => update(i, { diseaseSlug })}
                />
              </td>
              <td className="cell-narrow">
                <NumberInput
                  value={segment.movement}
                  min={0}
                  max={12}
                  onChange={(value) => update(i, { movement: value === '' ? 0 : value })}
                />
              </td>
              <td className="cell-narrow">
                <NumberInput
                  value={segment.weight}
                  min={1}
                  max={100}
                  onChange={(value) => update(i, { weight: value === '' ? 1 : value })}
                />
              </td>
              <td>{totalWeight > 0 ? `${((segment.weight / totalWeight) * 100).toFixed(1)}%` : '—'}</td>
              <td>
                <button
                  className="button button-ghost"
                  onClick={() => setDraft(reindex(draft.filter((_, index) => index !== i)))}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="row">
        <button
          className="button button-ghost"
          onClick={() =>
            setDraft(
              reindex([
                ...draft,
                {
                  index: draft.length,
                  diseaseSlug: diseaseOptions[0]?.value ?? '',
                  movement: 1,
                  weight: 1,
                },
              ]),
            )
          }
          disabled={diseaseOptions.length === 0}
        >
          Add segment
        </button>
        <button className="button button-ghost" onClick={() => void wheel.reload()}>
          Discard changes
        </button>
      </div>

      <Status error={error} message={message} />
    </Panel>
  );
}
